import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { API } from '../../utils/api';
import ConfirmModal from '../../components/modals/ConfirmModal';
import SuccessModal from '../../components/modals/SuccessModal';
import ErrorModal from '../../components/modals/ErrorModal';
import { 
  getWeekNumber, 
  getWeekDateRange, 
  getCurrentWeek
} from '../../utils/weekCalculations';

const AdminOvertimePage = () => {
  const [overtimeRequests, setOvertimeRequests] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedTab, setSelectedTab] = useState('pending');

  // Get current week data using shared utility
  const currentWeekData = getCurrentWeek();

  // Date filter state - using From/To date range
  const [fromYear, setFromYear] = useState(currentWeekData.year);
  const [fromWeek, setFromWeek] = useState(currentWeekData.week);
  const [toYear, setToYear] = useState(currentWeekData.year);
  const [toWeek, setToWeek] = useState(currentWeekData.week);
  
  // Calendar state
  const [showFromCalendar, setShowFromCalendar] = useState(false);
  const [showToCalendar, setShowToCalendar] = useState(false);

  // Bulk actions state
  const [selectedItems, setSelectedItems] = useState([]);
  const [selectedSection, setSelectedSection] = useState(null);
  const [showBulkActions, setShowBulkActions] = useState(false);
  const [showDenialModal, setShowDenialModal] = useState(false);
  const [denialReason, setDenialReason] = useState('');

  // Custom modal states
  const [confirmModal, setConfirmModal] = useState({ show: false, message: '', onConfirm: null });
  const [successModal, setSuccessModal] = useState({ show: false, message: '' });
  const [errorModal, setErrorModal] = useState({ show: false, message: '' });

  const modalRef = useRef(null);

  // Format week display using shared utility
  function formatWeekDisplay(year, weekNum) {
    const { startStr, endStr } = getWeekDateRange(year, weekNum);
    return `${year} Week ${weekNum} (${startStr} - ${endStr})`;
  }

  // Handle date click in calendar - matching Team Performance/Expenses page logic
  function handleDateClick(date, isFrom = true) {
    const year = date.getFullYear();
    const weekNum = getWeekNumber(date, year);
    
    if (isFrom) {
      setFromYear(year);
      setFromWeek(weekNum);
      setShowFromCalendar(false);
      
      const fromRange = getWeekDateRange(year, weekNum);
      const toRange = getWeekDateRange(toYear, toWeek);
      
      if (fromRange.start > toRange.start) {
        setToYear(year);
        setToWeek(weekNum);
      }
    } else {
      const toRange = getWeekDateRange(year, weekNum);
      const fromRange = getWeekDateRange(fromYear, fromWeek);
      
      if (toRange.start < fromRange.start) {
        setFromYear(year);
        setFromWeek(weekNum);
      }
      
      setToYear(year);
      setToWeek(weekNum);
      setShowToCalendar(false);
    }
  }

  // Generate calendar for a specific month - matching other pages
  function generateCalendar(currentDate) {
    const year = currentDate.getFullYear();
    const month = currentDate.getMonth();
    const firstDay = new Date(year, month, 1);
    const startDate = new Date(firstDay);
    
    const dayOfWeek = firstDay.getDay();
    const daysToSubtract = dayOfWeek === 0 ? 6 : dayOfWeek - 1;
    startDate.setDate(startDate.getDate() - daysToSubtract);
    
    const weeks = [];
    let currentWeek = [];
    
    for (let i = 0; i < 42; i++) {
      const date = new Date(startDate);
      date.setDate(startDate.getDate() + i);
      
      currentWeek.push(date);
      
      if (currentWeek.length === 7) {
        weeks.push(currentWeek);
        currentWeek = [];
      }
    }
    
    return { weeks, month, year };
  }

  // Go to current week
  const goToCurrentWeek = () => {
    const { year, week } = getCurrentWeek();
    setFromYear(year);
    setFromWeek(week);
    setToYear(year);
    setToWeek(week);
  };

  // Calendar Component - Dark Theme matching Expenses page
  const Calendar = ({ onDateClick, onClose, selectedYear, selectedWeek, isFromCalendar }) => {
    // Initialize display date based on the selected week (not current date)
    const initialDisplayDate = () => {
      const weekRange = getWeekDateRange(selectedYear, selectedWeek);
      return weekRange.start;
    };
    const [displayDate, setDisplayDate] = useState(initialDisplayDate);
    const { weeks, month, year } = generateCalendar(displayDate);
    const calendarRef = useRef(null);
    
    useEffect(() => {
      const handleClickOutside = (event) => {
        if (calendarRef.current && !calendarRef.current.contains(event.target)) {
          onClose();
        }
      };
      
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [onClose]);

    const monthNames = [
      'January', 'February', 'March', 'April', 'May', 'June',
      'July', 'August', 'September', 'October', 'November', 'December'
    ];
    
    const navigateMonth = (direction) => {
      const newDate = new Date(displayDate);
      newDate.setMonth(newDate.getMonth() + direction);
      
      const today = new Date();
      const maxDate = new Date(today.getFullYear(), today.getMonth() + 1, 1);
      
      if (direction > 0 && newDate > maxDate) {
        return;
      }
      
      setDisplayDate(newDate);
    };
    
    const isSelectedWeek = (date) => {
      const year = date.getFullYear();
      const weekNum = getWeekNumber(date, year);
      return year === selectedYear && weekNum === selectedWeek;
    };
    
    return (
      <div className="relative">
        <div className={`absolute z-[9999] mt-2 ${isFromCalendar ? 'left-0' : 'right-0'}`}>
          <div 
            ref={calendarRef}
            className="bg-slate-800 rounded-xl shadow-xl border border-slate-600 p-6 w-80"
          >
            {/* Month Navigation */}
            <div className="flex items-center justify-between mb-4">
              <button
                onClick={() => navigateMonth(-1)}
                className="p-2 hover:bg-slate-700 rounded-lg transition-colors"
              >
                <svg className="w-5 h-5 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
              </button>
              <h3 className="text-lg font-semibold text-white">
                {monthNames[month]} {year}
              </h3>
              <button
                onClick={() => navigateMonth(1)}
                className="p-2 hover:bg-slate-700 rounded-lg transition-colors"
              >
                <svg className="w-5 h-5 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </button>
            </div>

            {/* Weekday Headers */}
            <div className="grid grid-cols-7 gap-1 mb-2">
              {['M', 'T', 'W', 'T', 'F', 'S', 'S'].map((day, index) => (
                <div key={index} className="text-center text-xs font-semibold text-gray-400 py-2">
                  {day}
                </div>
              ))}
            </div>

            {/* Calendar Grid */}
            <div className="space-y-1">
              {weeks.map((week, weekIndex) => (
                <div key={weekIndex} className="grid grid-cols-7 gap-1">
                  {week.map((date, dateIndex) => {
                    const isCurrentMonth = date.getMonth() === month;
                    const isToday = date.toDateString() === new Date().toDateString();
                    const isSelected = isSelectedWeek(date);
                    const isFutureDate = date > new Date();
                    
                    return (
                      <button
                        key={dateIndex}
                        onClick={() => !isFutureDate && onDateClick(date)}
                        disabled={isFutureDate}
                        className={`
                          p-2 text-sm rounded-lg transition-all duration-200 hover:scale-105
                          ${isFutureDate 
                            ? 'text-gray-600 cursor-not-allowed opacity-50' 
                            : isCurrentMonth 
                              ? 'text-white hover:bg-slate-700' 
                              : 'text-gray-500'
                          }
                          ${isSelected ? 'bg-purple-600 text-white hover:bg-purple-700' : ''}
                          ${isToday && !isSelected ? 'ring-2 ring-purple-400' : ''}
                        `}
                      >
                        {date.getDate()}
                      </button>
                    );
                  })}
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  };

  // Fetch overtime requests from API
  useEffect(() => {
    fetchOvertimeRequests();
  }, [fromYear, fromWeek, toYear, toWeek]);

  const fetchOvertimeRequests = async () => {
    setIsLoading(true);
    try {
      // Get date range for API call using shared utility
      const fromRange = getWeekDateRange(fromYear, fromWeek);
      const toRange = getWeekDateRange(toYear, toWeek);
      const startDate = fromRange.startISO;
      const endDate = toRange.endISO;
      
      // Fetch pending overtime requests (always show regardless of date range)
      const pendingResponse = await axios.get(`${API}/admin/overtime`, {
        params: {
          status: 'pending'
        },
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      
      // Fetch approved and denied overtime requests within date range
      const dateFilteredResponse = await axios.get(`${API}/admin/overtime`, {
        params: {
          start_date: startDate,
          end_date: endDate
        },
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      
      // Combine results, filtering out pending from date-filtered results to avoid duplicates
      const pendingRequests = pendingResponse.data || [];
      const dateFilteredRequests = (dateFilteredResponse.data || []).filter(req => req.status !== 'pending');
      
      setOvertimeRequests([...pendingRequests, ...dateFilteredRequests]);
    } catch (error) {
      console.error('Error fetching overtime requests:', error);
      setOvertimeRequests([]);
    }
    setIsLoading(false);
  };

  const handleStatusChange = async (id, newStatus, denialReason = '') => {
    try {
      // Update local state immediately for better UX
      setOvertimeRequests(prev => prev.map(request => 
        request.id === id 
          ? { ...request, status: newStatus, denial_reason: denialReason, approval_date: newStatus === 'approved' ? new Date().toISOString().split('T')[0] : null }
          : request
      ));

      // Make API call to update status
      await axios.put(`${API}/admin/overtime/${id}`, null, {
        params: { 
          status: newStatus,
          denial_reason: denialReason 
        },
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });

    } catch (error) {
      console.error('Error updating overtime status:', error);
      // Revert state on error
      setOvertimeRequests(prev => prev.map(request => 
        request.id === id 
          ? { ...request, status: 'pending', denial_reason: '', approval_date: null }
          : request
      ));
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-GB', {
      style: 'currency',
      currency: 'GBP'
    }).format(amount);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-GB');
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'approved':
        return <span className="px-2 py-1 text-xs font-semibold rounded-full bg-green-500/20 text-green-300 border border-green-500/30">Approved</span>;
      case 'pending':
        return <span className="px-2 py-1 text-xs font-semibold rounded-full bg-yellow-500/20 text-yellow-300 border border-yellow-500/30">Pending</span>;
      case 'denied':
        return <span className="px-2 py-1 text-xs font-semibold rounded-full bg-red-500/20 text-red-300 border border-red-500/30">Denied</span>;
      default:
        return <span className="px-2 py-1 text-xs font-semibold rounded-full bg-slate-600 text-gray-300">Unknown</span>;
    }
  };

  const getPaymentStatusBadge = (paymentStatus) => {
    switch (paymentStatus) {
      case 'paid':
        return <span className="px-2 py-1 text-xs font-semibold rounded-full bg-green-500/20 text-green-300 border border-green-500/30">Paid</span>;
      case 'unpaid':
      default:
        return <span className="px-2 py-1 text-xs font-semibold rounded-full bg-yellow-500/20 text-yellow-300 border border-yellow-500/30">Unpaid</span>;
    }
  };

  // Filter overtime requests by status
  const filteredRequests = overtimeRequests.filter(request => {
    if (selectedTab === 'all') return true;
    return request.status === selectedTab;
  });

  // Calculate summary statistics
  const pendingCount = overtimeRequests.filter(req => req.status === 'pending').length;
  const approvedCount = overtimeRequests.filter(req => req.status === 'approved').length;
  const deniedCount = overtimeRequests.filter(req => req.status === 'denied').length;

  // Bulk actions handlers
  const handleItemSelect = (itemId) => {
    setSelectedItems(prev => {
      const newSelection = prev.includes(itemId) 
        ? prev.filter(id => id !== itemId)
        : [...prev, itemId];
      
      if (newSelection.length > 0) {
        setSelectedSection(selectedTab);
        setShowBulkActions(true);
      } else {
        setSelectedSection(null);
        setShowBulkActions(false);
      }
      
      return newSelection;
    });
  };

  const handleSelectAll = () => {
    const currentTabItems = filteredRequests.map(req => req.id);
    if (selectedItems.length === currentTabItems.length) {
      setSelectedItems([]);
      setShowBulkActions(false);
      setSelectedSection(null);
    } else {
      setSelectedItems(currentTabItems);
      setSelectedSection(selectedTab);
      setShowBulkActions(true);
    }
  };

  const clearSelection = () => {
    setSelectedItems([]);
    setShowBulkActions(false);
    setSelectedSection(null);
  };

  // Get available bulk actions based on selected section and payment status
  const getAvailableBulkActions = () => {
    if (!selectedSection || selectedItems.length === 0) return [];

    const selectedOvertimeRequests = overtimeRequests.filter(req => selectedItems.includes(req.id));
    const actions = [];

    if (selectedSection === 'pending') {
      actions.push('approve', 'deny');
    } else if (selectedSection === 'approved') {
      // Only allow deny if ALL selected overtime requests are unpaid
      const allUnpaid = selectedOvertimeRequests.every(req => req.payment_status !== 'paid');
      if (allUnpaid) {
        actions.push('deny');
      }
    } else if (selectedSection === 'denied') {
      // Allow re-approving denied overtime
      actions.push('approve');
    }

    return actions;
  };

  const handleBulkAction = async (action) => {
    if (selectedItems.length === 0) return;

    try {
      if (action === 'deny') {
        setShowDenialModal(true);
        return;
      } else {
        const statusMap = {
          approve: 'approved'
        };

        await axios.put(`${API}/admin/overtime/bulk-status`, {
          overtime_ids: selectedItems,
          status: statusMap[action]
        }, {
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
        });
        
        setOvertimeRequests(prev => prev.map(req => 
          selectedItems.includes(req.id) 
            ? { 
                ...req, 
                status: statusMap[action], 
                approval_date: statusMap[action] === 'approved' ? new Date().toISOString().split('T')[0] : null,
                denial_reason: null
              }
            : req
        ));
      }

      clearSelection();

    } catch (error) {
      console.error(`Error in bulk ${action}:`, error);
      setErrorModal({
        show: true,
        message: `Failed to ${action} overtime requests. Please try again.`
      });
    }
  };

  const submitBulkDenial = async () => {
    if (!denialReason.trim()) {
      setErrorModal({
        show: true,
        message: 'Please provide a reason for denial.'
      });
      return;
    }

    try {
      await axios.put(`${API}/admin/overtime/bulk-status`, {
        overtime_ids: selectedItems,
        status: 'denied',
        denial_reason: denialReason
      }, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      
      setOvertimeRequests(prev => prev.map(req => 
        selectedItems.includes(req.id) 
          ? { ...req, status: 'denied', denial_reason: denialReason, approval_date: null }
          : req
      ));
      
      clearSelection();
      setShowDenialModal(false);
      setDenialReason('');

      setSuccessModal({
        show: true,
        message: 'Overtime requests denied successfully!'
      });
    } catch (error) {
      console.error('Error in bulk deny:', error);
      setErrorModal({
        show: true,
        message: 'Failed to deny overtime requests. Please try again.'
      });
    }
  };

  return (
    <div className="min-h-screen bg-slate-900 -m-8 p-8">
      {/* Header */}
      <div className="bg-gradient-to-r from-purple-600 via-purple-700 to-indigo-700 rounded-xl p-6 text-white mb-6">
        <h1 className="text-2xl sm:text-3xl font-bold mb-1">Overtime</h1>
        <p className="text-purple-200">Review and approve overtime requests</p>
      </div>

      {/* Filters Section - Dark Theme */}
      <div className="bg-slate-800 rounded-xl border border-slate-700 p-4 mb-6 overflow-visible">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center">
            <div className="w-8 h-8 bg-purple-600 rounded-lg flex items-center justify-center">
              <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.707A1 1 0 013 7V4z" />
              </svg>
            </div>
            <h2 className="text-lg font-semibold text-white ml-3">Filters & Date Range</h2>
          </div>
          
          <button
            onClick={goToCurrentWeek}
            className="bg-purple-600 hover:bg-purple-500 text-white px-4 py-2 rounded-lg font-medium transition-all duration-200 text-sm flex items-center"
          >
            <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 002 2z" />
            </svg>
            Current Week
          </button>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* From Filter */}
          <div className="bg-slate-700/50 rounded-lg p-3 border border-slate-600 relative">
            <h4 className="text-xs font-bold text-gray-300 mb-2 flex items-center">
              <svg className="w-3 h-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 002 2z" />
              </svg>
              From Date
            </h4>
            <div className="relative">
              <button
                onClick={() => setShowFromCalendar(true)}
                className="w-full px-3 py-2 bg-slate-800 border border-slate-600 rounded-lg text-sm text-white focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent text-left flex items-center justify-between hover:bg-slate-700"
              >
                <span className="truncate">{formatWeekDisplay(fromYear, fromWeek)}</span>
                <svg className="w-4 h-4 text-gray-400 ml-1 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 002 2z" />
                </svg>
              </button>
              
              {showFromCalendar && (
                <Calendar
                  onDateClick={(date) => handleDateClick(date, true)}
                  onClose={() => setShowFromCalendar(false)}
                  selectedYear={fromYear}
                  selectedWeek={fromWeek}
                  isFromCalendar={true}
                />
              )}
            </div>
          </div>

          {/* To Filter */}
          <div className="bg-slate-700/50 rounded-lg p-3 border border-slate-600 relative">
            <h4 className="text-xs font-bold text-gray-300 mb-2 flex items-center">
              <svg className="w-3 h-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 002 2z" />
              </svg>
              To Date
            </h4>
            <div className="relative">
              <button
                onClick={() => setShowToCalendar(true)}
                className="w-full px-3 py-2 bg-slate-800 border border-slate-600 rounded-lg text-sm text-white focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent text-left flex items-center justify-between hover:bg-slate-700"
              >
                <span className="truncate">{formatWeekDisplay(toYear, toWeek)}</span>
                <svg className="w-4 h-4 text-gray-400 ml-1 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 002 2z" />
                </svg>
              </button>
              
              {showToCalendar && (
                <Calendar
                  onDateClick={(date) => handleDateClick(date, false)}
                  onClose={() => setShowToCalendar(false)}
                  selectedYear={toYear}
                  selectedWeek={toWeek}
                  isFromCalendar={false}
                />
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Bulk Actions Section - Dark Theme */}
      {selectedItems.length > 0 && (
        <div className="mb-6 bg-purple-500/10 rounded-xl p-4 border border-purple-500/30">
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <div className="w-8 h-8 bg-purple-600 rounded-lg flex items-center justify-center mr-3">
                <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                </svg>
              </div>
              <div className="text-lg font-semibold text-white">
                Bulk Actions ({selectedItems.length} items selected)
              </div>
              <div className="ml-2 text-sm text-purple-400 font-medium capitalize">
                from {selectedSection} overtime
              </div>
            </div>
            <div className="flex items-center space-x-3">
              {getAvailableBulkActions().includes('approve') && (
                <button
                  onClick={() => handleBulkAction('approve')}
                  className="bg-green-600 hover:bg-green-500 text-white px-4 py-2 rounded-lg transition-all duration-200 flex items-center"
                >
                  <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                  Approve
                </button>
              )}
              {getAvailableBulkActions().includes('deny') && (
                <button
                  onClick={() => handleBulkAction('deny')}
                  className="bg-red-600 hover:bg-red-500 text-white px-4 py-2 rounded-lg transition-all duration-200 flex items-center"
                >
                  <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                  Deny
                </button>
              )}
              <button
                onClick={clearSelection}
                className="text-gray-300 hover:text-white px-3 py-2 rounded-lg border border-slate-600 hover:bg-slate-700 transition-colors"
              >
                Clear Selection
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Tabs - Dark Theme */}
      <div className="bg-slate-800 rounded-xl border border-slate-700 overflow-hidden">
        <div className="border-b border-slate-700">
          <nav className="-mb-px flex space-x-8 px-6">
            {['pending', 'approved', 'denied'].map((tab) => (
              <button
                key={tab}
                onClick={() => {
                  setSelectedTab(tab);
                  clearSelection();
                }}
                className={`py-4 px-1 border-b-2 font-medium text-sm ${
                  selectedTab === tab
                    ? 'border-purple-500 text-purple-400'
                    : 'border-transparent text-gray-400 hover:text-gray-300 hover:border-slate-600'
                }`}
              >
                {tab.charAt(0).toUpperCase() + tab.slice(1)}
                <span className={`ml-2 px-2 py-1 text-xs rounded-full ${
                  tab === 'pending' ? 'bg-yellow-500/20 text-yellow-300' :
                  tab === 'approved' ? 'bg-green-500/20 text-green-300' :
                  'bg-red-500/20 text-red-300'
                }`}>
                  {tab === 'pending' ? pendingCount : tab === 'approved' ? approvedCount : deniedCount}
                </span>
              </button>
            ))}
          </nav>
        </div>

        {/* Table - Dark Theme */}
        {isLoading ? (
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-500"></div>
            <span className="ml-3 text-gray-400">Loading overtime requests...</span>
          </div>
        ) : filteredRequests.length === 0 ? (
          <div className="text-center py-12">
            <div className="w-16 h-16 bg-slate-700 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg className="w-8 h-8 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <h3 className="text-lg font-medium text-white mb-2">No {selectedTab} overtime requests</h3>
            <p className="text-gray-400">No overtime requests found for the selected criteria.</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full">
              <thead className="bg-slate-700">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-400 uppercase tracking-wider w-12">
                    <input
                      type="checkbox"
                      checked={filteredRequests.length > 0 && filteredRequests.every(req => selectedItems.includes(req.id))}
                      onChange={handleSelectAll}
                      className="w-4 h-4 text-purple-600 bg-slate-700 border-slate-500 rounded focus:ring-purple-500"
                    />
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-400 uppercase tracking-wider">Employee</th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-400 uppercase tracking-wider">Date</th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-400 uppercase tracking-wider">Time Period</th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-400 uppercase tracking-wider">Site</th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-400 uppercase tracking-wider">Hours</th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-400 uppercase tracking-wider">Total</th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-400 uppercase tracking-wider">
                    {selectedTab === 'denied' ? 'Overtime Reason' : 'Reason'}
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-400 uppercase tracking-wider">Status</th>
                  {selectedTab === 'approved' && (
                    <th className="px-6 py-3 text-left text-xs font-semibold text-gray-400 uppercase tracking-wider">Payment Status</th>
                  )}
                  {selectedTab === 'denied' && (
                    <th className="px-6 py-3 text-left text-xs font-semibold text-gray-400 uppercase tracking-wider">Reason for Denial</th>
                  )}
                  {selectedTab === 'pending' && (
                    <th className="px-6 py-3 text-left text-xs font-semibold text-gray-400 uppercase tracking-wider">Actions</th>
                  )}
                </tr>
              </thead>
              <tbody>
                {filteredRequests.map((request, index) => (
                  <tr key={request.id} className={`hover:bg-slate-700/50 transition-colors ${selectedItems.includes(request.id) ? 'bg-purple-500/10' : ''} ${index !== filteredRequests.length - 1 ? 'border-b border-slate-700/50' : ''}`}>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <input
                        type="checkbox"
                        checked={selectedItems.includes(request.id)}
                        onChange={() => handleItemSelect(request.id)}
                        className="w-4 h-4 text-purple-600 bg-slate-700 border-slate-500 rounded focus:ring-purple-500"
                      />
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <div className="flex-shrink-0 h-8 w-8">
                          <div className="h-8 w-8 rounded-full bg-gradient-to-br from-purple-500 to-indigo-600 flex items-center justify-center">
                            <span className="text-sm font-medium text-white">
                              {request.employee_name?.charAt(0).toUpperCase() || request.user?.first_name?.charAt(0).toUpperCase() || 'U'}
                            </span>
                          </div>
                        </div>
                        <div className="ml-4">
                          <div className="text-sm font-medium text-white">
                            {request.employee_name || `${request.user?.first_name || ''} ${request.user?.last_name || ''}`.trim() || 'Unknown Employee'}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-300">{formatDate(request.date)}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-300">
                      {request.start_time} - {request.end_time}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-300">{request.site || 'N/A'}</td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-purple-400">{request.hours}h</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-white">
                        {formatCurrency(request.total_amount || 0)}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-300 max-w-xs truncate" title={request.reason}>
                        {request.reason}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">{getStatusBadge(request.status)}</td>
                    
                    {selectedTab === 'approved' && (
                      <td className="px-6 py-4 whitespace-nowrap">
                        {getPaymentStatusBadge(request.payment_status)}
                      </td>
                    )}
                    
                    {selectedTab === 'denied' && (
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-xs text-red-400 bg-red-500/10 border border-red-500/30 p-2 rounded max-w-xs">
                          {request.denial_reason || 'No reason provided'}
                        </div>
                      </td>
                    )}
                    
                    {selectedTab === 'pending' && (
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium space-x-2">
                        <button
                          onClick={() => handleStatusChange(request.id, 'approved')}
                          className="text-green-400 hover:text-green-300 px-3 py-1 rounded bg-green-500/10 hover:bg-green-500/20 border border-green-500/30"
                        >
                          Approve
                        </button>
                        <button
                          onClick={() => {
                            setSelectedItems([request.id]);
                            setSelectedSection('pending');
                            setShowDenialModal(true);
                          }}
                          className="text-red-400 hover:text-red-300 px-3 py-1 rounded bg-red-500/10 hover:bg-red-500/20 border border-red-500/30"
                        >
                          Deny
                        </button>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Bulk Denial Modal - Dark Theme */}
      {showDenialModal && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50">
          <div ref={modalRef} className="bg-slate-900 rounded-2xl shadow-2xl w-full max-w-md mx-4 border border-slate-700">
            {/* Modal Header */}
            <div className="bg-gradient-to-r from-red-600 to-red-700 px-6 py-4 flex items-center justify-between rounded-t-2xl">
              <h3 className="text-xl font-bold text-white">Deny Overtime Requests</h3>
              <button
                onClick={() => {
                  setShowDenialModal(false);
                  setDenialReason('');
                }}
                className="text-white/80 hover:text-white hover:bg-white/10 rounded-lg p-2 transition-all duration-200"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            {/* Modal Body */}
            <div className="p-6">
              <div className="mb-4">
                <p className="text-gray-300 mb-2">
                  Deny <span className="font-semibold text-red-400">{selectedItems.length} selected overtime request(s)</span>
                </p>
                <p className="text-sm text-gray-400">Please provide a reason for the denial:</p>
              </div>

              <div className="space-y-3">
                <textarea
                  value={denialReason}
                  onChange={(e) => setDenialReason(e.target.value)}
                  rows={4}
                  className="w-full px-3 py-2 bg-slate-800 border border-slate-600 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent resize-none"
                  placeholder="Enter reason for denial..."
                />
              </div>

              {/* Modal Actions */}
              <div className="flex items-center justify-end space-x-4 pt-6 border-t border-slate-700 mt-6">
                <button
                  onClick={() => {
                    setShowDenialModal(false);
                    setDenialReason('');
                  }}
                  className="px-6 py-2 text-gray-300 border border-slate-600 rounded-lg hover:bg-slate-700 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={submitBulkDenial}
                  disabled={!denialReason.trim()}
                  className={`px-6 py-2 rounded-lg transition-all duration-200 ${
                    denialReason.trim()
                      ? 'bg-red-600 text-white hover:bg-red-500'
                      : 'bg-slate-700 text-gray-500 cursor-not-allowed'
                  }`}
                >
                  Deny Selected
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Custom Modals */}
      <ConfirmModal
        isOpen={confirmModal.show}
        message={confirmModal.message}
        onConfirm={() => {
          if (confirmModal.onConfirm) {
            confirmModal.onConfirm();
          }
          setConfirmModal({ show: false, message: '', onConfirm: null });
        }}
        onClose={() => setConfirmModal({ show: false, message: '', onConfirm: null })}
      />

      <SuccessModal
        isOpen={successModal.show}
        message={successModal.message}
        onClose={() => setSuccessModal({ show: false, message: '' })}
      />

      <ErrorModal
        isOpen={errorModal.show}
        message={errorModal.message}
        onClose={() => setErrorModal({ show: false, message: '' })}
      />
    </div>
  );
};

export default AdminOvertimePage;
